import React, { useState, useEffect, useRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useIManage } from '@/hooks/useIManage';
import toast from 'react-hot-toast';
import { backendClient } from '@/lib/api-client';
import { Loading } from '../shared';

interface IManageFilePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onFilesSelected?: (files: any[]) => void;
  matterId?: string;
}

export const IManageFilePicker: React.FC<IManageFilePickerProps> = ({
  isOpen,
  onClose,
  onFilesSelected,
  matterId,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const {
    isAuthenticated,
    authData,
    isLoading,
    getFilePickerConfig,
    checkStoredAuth,
    isTokenExpired,
    refreshToken
  } = useIManage();

  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [isLoadingPicker, setIsLoadingPicker] = useState(false);
  const [tokenExpiryTime, setTokenExpiryTime] = useState<Date | null>(null);

  // Calculate token expiry time
  useEffect(() => {
    if (authData?.expires_in) {
      const expiryTime = new Date(Date.now() + authData.expires_in * 1000);
      setTokenExpiryTime(expiryTime);
      console.log('Token will expire at:', expiryTime.toLocaleString());
    }
  }, [authData]);

  // Check stored auth on mount
  useEffect(() => {
    checkStoredAuth();
  }, [checkStoredAuth]);


  // Check stored auth and handle dialog open/close
  useEffect(() => {
    console.log('Dialog state changed - isOpen:', isOpen, 'isAuthenticated:', isAuthenticated, 'authData:', authData);
    if (isOpen) {
      // Always check stored auth when dialog opens
      console.log('Dialog opened, checking stored auth...');
      const hasAuth = checkStoredAuth();

      // Try to get auth data directly from session storage as backup
      const storedAuth = sessionStorage.getItem('imanage_auth');
      if (storedAuth) {
        try {
          const parsedAuth = JSON.parse(storedAuth);
          console.log('Direct session storage check - found auth:', parsedAuth);

          // If we have valid auth in storage but state isn't updated yet, use it directly
          if (parsedAuth.access_token && parsedAuth.customer_id && !isAuthenticated) {
            console.log('Using auth directly from storage since state not updated yet');
            // Load file picker directly with stored auth
            loadFilePicker(parsedAuth);
            return;
          }
        } catch (e) {
          console.error('Failed to parse stored auth:', e);
        }
      }

      // Small delay to let auth state update from checkStoredAuth
      const timer = setTimeout(() => {
        console.log('After delay - isAuthenticated:', isAuthenticated, 'authData:', authData);
        if (!isAuthenticated) {
          console.log('Not authenticated, initiating OAuth login...');
          handleDirectOAuthLogin();
        } else if (authData) {
          console.log('Already authenticated, loading file picker with authData:', authData);
          // Already authenticated, load the file picker
          loadFilePicker(authData);
        } else {
          console.log('Authenticated but no authData available');
        }
      }, 200); // Increased delay slightly

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const loadFilePicker = async (overrideAuthData?: any) => {
    let authToUse = overrideAuthData || authData;
    console.log('loadFilePicker called, authData:', {
      has_access_token: !!authToUse?.access_token,
      has_customer_id: !!authToUse?.customer_id,
      customer_id_type: typeof authToUse?.customer_id,
      customer_id_value: authToUse?.customer_id,
      user_id: authToUse?.user_id,
      full_auth: authToUse
    });

    if (!authToUse?.access_token || !authToUse?.customer_id) {
      console.error('Missing auth data for file picker', {
        access_token: authToUse?.access_token?.substring(0, 20) + '...',
        customer_id: authToUse?.customer_id,
        user_id: authToUse?.user_id
      });
      toast.error('Please authenticate with iManage first');
      return;
    }

    // Check if token needs refresh before making the API call
    if (authToUse) {
      console.log('Checking if token is expired...');
      if (isTokenExpired(authToUse)) {
        console.log('Token expired, refreshing...');
        const refreshedAuth = await refreshToken();
        if (!refreshedAuth) {
          console.error('Failed to refresh token');
          toast.error('Session expired. Please authenticate with iManage again.');
          return;
        }
        authToUse = refreshedAuth;
        console.log('Token refreshed successfully');
      } else {
        console.log('Token is still valid, no refresh needed');
      }
    }

    setIsLoadingPicker(true);
    try {
      // Use the getFilePickerConfig from the hook, passing the current auth data
      const config = await getFilePickerConfig(authToUse);

      if (config) {
        console.log('iManage File Picker Config received:', {
          iframe_url: config.iframe_url,
          client_id: config.client_id,
          customer_id: config.customer_id,
          dialog_token: config.dialog_token
        });
        console.log('Setting iframe URL to:', config.iframe_url);
        setIframeUrl(config.iframe_url);
        console.log('Dialog should now be visible');
      } else {
        console.error('No config returned from getFilePickerConfig');
      }
    } catch (error: any) {
      console.error('Failed to load file picker:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error('Failed to load iManage file picker');
    } finally {
      setIsLoadingPicker(false);
      console.log('Finished loading picker, isLoadingPicker set to false');
    }
  };

  const handleDirectOAuthLogin = async () => {
    let authCompleted = false;
    let authWindow: Window | null = null;

    try {
      // Get the OAuth authorization URL from the backend
      const response = await backendClient.get('/imanage/oauth/authorize');
      const data = response.data;

      if (data.auth_url) {
        // Store state for verification
        sessionStorage.setItem('imanage_oauth_state', data.state);

        // Open OAuth login in a popup window
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        authWindow = window.open(
          data.auth_url,
          'iManageAuth',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        // Listen for auth success message from popup
        const handleMessage = async (event: MessageEvent) => {
          if (event.origin === window.location.origin && event.data.type === 'imanage-auth-success') {
            authCompleted = true;

            // Auth successful, handle the auth data
            if (event.data.authData) {
              // Process the authentication
              await handleAuthSuccess(event.data.authData);

              // Send acknowledgment back to popup so it can close
              if (authWindow && !authWindow.closed) {
                authWindow.postMessage({
                  type: 'imanage-auth-acknowledged'
                }, window.location.origin);
              }
            }

            window.removeEventListener('message', handleMessage);
          }
        };

        window.addEventListener('message', handleMessage);

        // Check if popup was blocked
        if (!authWindow || authWindow.closed) {
          toast.error('Popup blocked! Please allow popups for this site and try again.');
          onClose(); // Close the main dialog since we can't proceed
        }

        // Monitor if popup is closed without auth (user manually closed it)
        const checkInterval = setInterval(() => {
          if (authWindow && authWindow.closed) {
            clearInterval(checkInterval);
            window.removeEventListener('message', handleMessage);

            // Give a bit more time for auth to complete
            setTimeout(() => {
              // Only close dialog if auth was not completed
              if (!authCompleted && !isAuthenticated) {
                console.log('Popup closed without authentication');
                onClose();
              }
            }, 1000);
          }
        }, 500);
      }
    } catch (error) {
      console.error('Failed to initiate OAuth login:', error);
      toast.error('Failed to initiate iManage authentication. Please try again.');
      onClose(); // Close the main dialog since we can't proceed
    }
  };

  const handleAuthSuccess = async (authData: any) => {
    console.log('OAuth auth success received, authData:', authData);

    // Validate auth data
    if (!authData?.access_token || !authData?.customer_id) {
      console.error('Invalid auth data received from OAuth:', authData);
      toast.error('Authentication failed - missing required information. Please try again.');
      return;
    }

    // Store auth data in session storage (in case it wasn't stored by OAuth dialog)
    sessionStorage.setItem('imanage_auth', JSON.stringify(authData));
    console.log('Stored auth data in session storage');

    // Trigger re-check of auth status to update hook state
    checkStoredAuth();
    // Load the file picker immediately with the auth data
    await loadFilePicker(authData);
  };


  // Handle postMessage from iManage iframe
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // Verify the message is from iManage (allow both main domain and subdomains)
      if (!event.origin.match(/^https:\/\/([\w-]+\.)?cloudimanage\.com$/)) {
        return;
      }

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        console.log('iManage message received:', { type: data.type, data: data.data });

        if (data.type === 'ready') {
          console.log('iManage file picker is ready');
        } else if (data.type === 'preview' || data.type === 'open') {
          // Document preview requested - don't close dialog
          console.log('Document preview requested:', data.data);
          // The iframe handles the preview internally
        } else if (data.type === 'save' || data.type === 'select') {
          // User clicked "Select" button - this is the message sent when user confirms selection
          // Based on C# sample, 'select' message means files are selected and dialog should close
          const selectedFiles = data.data?.selected || data.data;

          if (selectedFiles) {
            console.log('Files selected:', selectedFiles);

            if (onFilesSelected) {
              onFilesSelected(selectedFiles);
            }

            onClose();
          } else {
            console.log('Selection message received but no files in data');
          }
        } else if (data.type === 'cancel') {
          // User cancelled the selection
          console.log('File selection cancelled');
          onClose();
        } else {
          console.log('Unknown message type from iManage:', data.type);
        }
      } catch (error) {
        console.error('Error handling postMessage:', error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [matterId, onFilesSelected, onClose]);

  const renderContent = () => {
    if (isLoadingPicker) {
      return (
        <div className="flex items-center justify-center h-[80vh]">
          <Loading text='Loading iManage file picker' />
        </div>
      );
    }

    if (iframeUrl) {
      return (
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          className="w-full h-[95vh] border-0"
          title="iManage File Picker"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
        />
      );
    }

    return null;
  };

  return (
    <>
      {/* File Picker Dialog with custom lighter overlay - show when authenticated, loading, or has iframe URL */}
      {(isAuthenticated || isLoadingPicker || iframeUrl) && (
        <DialogPrimitive.Root open={isOpen} onOpenChange={onClose}>
          <DialogPrimitive.Portal>
            {/* Ultra light overlay - only 10% opacity for maximum visibility */}
            <DialogPrimitive.Overlay
              className="fixed inset-0 z-100000 bg-black/10 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            />
            <DialogPrimitive.Content
              className="fixed left-[50%] top-[50%] z-100001 max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] translate-x-[-50%] translate-y-[-50%] border border-stone-200 bg-white shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-lg overflow-hidden p-0"
            >
              {renderContent()}
              {/* Custom close button - transparent to match iManage background */}
              <DialogPrimitive.Close className="absolute right-3 top-3 z-10 rounded-xs p-1.5 text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-gray-400 focus:ring-offset-2">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      )}
    </>
  );
};
